import {
  toWSDate,
  WSElement,
  WSFlexBox,
  WSGrid,
  WSList,
  WSPage,
  WSPageToolbar,
  WSPanel,
  WSSectionToolbar,
  WSTabs,
  WSText
} from "@wingspanhq/fe-component-library";
import {
  PayeeEngagementStatus,
  PayeeStatus
} from "@wingspanhq/wingspan-api-sdk";
import React from "react";
import { RouteComponentProps } from "react-router-dom";
import {
  queriesEngagements,
  queriesPayeeEngagements,
  queriesPayees,
  queriesRequirements,
  queriesWorkDefinitions
} from "@query";
import { useXWingspanPayerId } from "../useXWingspanPayerId";
import { useQueryFeatureFlags } from "@hooks/useQueryFeatureFlags";
import { selectorCounterpartyDisplayName } from "../../../../../shared/selectors/selectorCounterpartyDisplayName";
import { WSQueries } from "../../../../../query/WSQuery";
import { FieldView } from "../../../../../shared/components/FieldView/FieldView";
import { ResourceHistory } from "../../../../../shared/components/ResourceHistory";
import { useEngagementDefaultActions } from "../../../../Engagements/hooks/useEngagementDefaultActions";
import { PayeeIntegrationWidget } from "../../../../Payers/components/IntegrationsSection/PayeeIntegrationWidget";
import { RequirementsTable } from "../../../../Requirements/components/RequirementsTable";
import { RequirementContextType } from "../../../../Requirements/types";
import { sortRequirementsByPriority } from "../../../../Requirements/utils";
import { TableWorkDefinitions } from "../../../../WorkDefinitions";
import { WidgetWorkLogsPayerView } from "../../../../WorkLogs/components/WidgetWorkLogsPayerView";
import { usePayeeEngagementHistoryResources } from "../../../hooks/usePayeeEngagementHistoryResources";
import {
  buildEngagementHistoryPath,
  PAYEES_ROOT_PATH,
  useGoToPayeeDetails
} from "../../../utils";

type EngagementDetailsSidebarProps = RouteComponentProps<{
  engagementId: string;
  payeeId: string;
}>;

export const RoutePayeeEngagementDetails: React.FC<
  EngagementDetailsSidebarProps
> = ({
  match: {
    params: { engagementId, payeeId }
  },
  history
}) => {
  const queryFeatureFlags = useQueryFeatureFlags();
  const xWingspanAccount = useXWingspanPayerId();
  // Direct `getPayee`, not the `searchPayeeRows` projection. The search index is
  // currently unstable, and this screen gates its whole render on the read, so a
  // failing index took the page down with it. `Payee` carries the same `status`
  // the toolbar gates on; the trade is only the displayed name, since the row
  // carries the server-resolved `profile.resolvedName` and the entity does not.
  const queryPayee = queriesPayees.getPayee.useQuery({
    payeeId,
    xWingspanAccount
  });
  const queryEngagement = queriesPayeeEngagements.getPayeeEngagement.useQuery({
    payeeId,
    payeeEngagementId: engagementId,
    xWingspanAccount
  });
  // native instance read — replaces the v1 engagement's embedded requirements
  const queryRequirements =
    queriesRequirements.listPayeeRequirements.useQueryAll({
      filter: {
        payeeEngagementId: { in: [engagementId] }
      },
      xWingspanAccount
    });
  const fetchDefaultActions = useEngagementDefaultActions();

  const goToPayeeDetails = useGoToPayeeDetails();

  const historyResources = usePayeeEngagementHistoryResources(engagementId);
  const featureFlags = useQueryFeatureFlags();

  // Fetch the engagement type to get work definitions
  const engagementTypeId = queryEngagement.data?.engagementId;
  const queryEngagementType = queriesEngagements.getEngagement.useQuery(
    { engagementId: engagementTypeId!, xWingspanAccount },
    { enabled: !!engagementTypeId }
  );

  // Extract work definition IDs from engagement type (once loaded)
  const workDefinitionIds = queryEngagementType.data?.workDefinitionIds || [];

  // v3 `listWorkDefinitions` exposes no id-set filter or sort — fetch all and
  // narrow to the engagement's attached IDs client-side (proposal PE-3 fan-out).
  const queryWorkDefinitions =
    queriesWorkDefinitions.listWorkDefinitions.useQueryAll(undefined, {
      enabled: workDefinitionIds.length > 0
    });
  const workDefinitions = (queryWorkDefinitions.flatData ?? [])
    .filter(wd => !!wd.id && workDefinitionIds.includes(wd.id))
    .sort((a, b) => (a.name ?? "").localeCompare(b.name ?? ""));
  const hasWorkDefinitions = workDefinitions.length > 0;

  return (
    <WSPage>
      <WSQueries queries={{ queryEngagement, queryPayee }}>
        {({ queryEngagementData, queryPayeeData }) => {
          const requirements = sortRequirementsByPriority(
            queryRequirements.flatData ?? []
          );

          // `selectorPayerPayeeEngagementName(_, true)` only ever returned
          // `engagementName` (its `isDefault` guard applies to the `false` arm), and
          // that selector still serves the v1-typed payer-engagement + search-row
          // callers — so read the v3 field directly rather than widening it.
          const engagementName =
            queryEngagementData.engagementName ?? undefined;

          const isInactive =
            queryEngagementData.status === PayeeEngagementStatus.SUSPENDED ||
            queryEngagementData.status === PayeeEngagementStatus.TERMINATED;

          const payeeName = selectorCounterpartyDisplayName(queryPayeeData);

          const requirementsTable = (
            <RequirementsTable
              requirements={requirements}
              contextType={RequirementContextType.Payee}
              onViewDetails={async requirement => {
                if (requirement.id) {
                  history.push(
                    `/member/payees/${payeeId}/requirement/${requirement.id}?engagementId=${queryEngagementData.id}`
                  );
                }
              }}
            />
          );

          return (
            <WSList>
              <WSPageToolbar
                title={engagementName}
                titleAfter={
                  <WSText.ParagraphXs color="gray500">
                    Last modified{" "}
                    {
                      toWSDate(queryEngagementData.events?.updatedAt)
                        .formattedValue
                    }
                  </WSText.ParagraphXs>
                }
                fetchActions={
                  queryPayeeData.status === PayeeStatus.ACTIVATED
                    ? () =>
                        fetchDefaultActions({
                          engagement: queryEngagementData,
                          payeeId,
                          payeeName
                        })
                    : undefined
                }
                titlePill={
                  isInactive
                    ? {
                        theme: "neutral",
                        text: "Archived",
                        icon: false
                      }
                    : undefined
                }
                breadcrumbs={[
                  {
                    label: "Contractors",
                    onClick: () => {
                      history.push(PAYEES_ROOT_PATH);
                    }
                  },
                  {
                    label: payeeName,
                    onClick: () => {
                      goToPayeeDetails(payeeId);
                    }
                  },
                  {
                    label: engagementName || "Assignment"
                  }
                ]}
              />
              <WSGrid>
                <WSGrid.Item span={{ m: "12", l: "8" }}>
                  <WSList gap="L">
                    <WSElement>
                      <WSSectionToolbar title="Assignment information" />
                      <WSPanel>
                        <WSFlexBox gap="XL">
                          <FieldView
                            label="Engagement name"
                            value={engagementName}
                          />
                          {/*
                   <FieldView
                      label="Type"
                      value={getEngagementLabelByType(
                        queryEngagementData.type,
                        {
                          descriptive: true
                        }
                      )}
                    />
                    */}
                        </WSFlexBox>
                        {/* <FieldView
                  mt="XL"
                  label="Description"
                  value={"TODO:description"}
                /> */}
                      </WSPanel>
                    </WSElement>
                    {featureFlags.data?.displayWorkDefinitions &&
                    hasWorkDefinitions ? (
                      <WSTabs
                        tabs={[
                          {
                            label: "Work definitions",
                            render: () => (
                              <TableWorkDefinitions
                                workDefinitions={workDefinitions}
                              />
                            )
                          },
                          {
                            label: "Requirements",
                            render: () => requirementsTable
                          }
                        ]}
                      />
                    ) : (
                      <WSElement>
                        <WSElement>
                          <WSSectionToolbar title="Assignment requirements" />

                          {requirementsTable}
                        </WSElement>
                      </WSElement>
                    )}
                  </WSList>
                </WSGrid.Item>
                <WSGrid.Item span={{ m: "12", l: "4" }}>
                  <WSList gap="L">
                    {/*
                      Disabled pending the v3 rate-card re-model — NOT left calling
                      v1, whose `servicePayments` reads throw `NotMigrated`.

                      The v3 ENDPOINTS exist (SDK 3.52.6):
                      `PayeeEngagementsApi.getPayeeEngagementRateCard` and
                      `.updatePayeeEngagementRateCard`, plus `RateCardsApi`
                      get/create/update. What blocks a straight swap is the MODEL:
                        - v1 `IRateCard.values` is `Record<string, IRateValue>` with
                          `{ key, value, name }` — a keyed map of named rate lines;
                        - v3 `RateCard.rates` is `RateCardTier[]` with
                          `{ id?, description?, unitType, unitCost }` — an array
                          keyed by id, with a REQUIRED `unitType` v1 has no analog
                          for and no free-form `name`.
                      The table, the edit form and the custom-vs-standard diff are
                      all built on the keyed-map shape, so this is a re-model of the
                      rate-card cluster (~983 lines across RateCardViewWidget,
                      RateCardTable, RouteEditRateCard and formatRateCardData), and
                      it also serves the engagement-type and work-log screens.
                      v1's merge provenance (`engagementRateCardId` /
                      `payerPayeeEngagementRateCardId`) is reconstructable on v3 by
                      comparing the effective card's id against
                      `Engagement.rateCardId`.

                      Was gated behind the `rateCardOverride` feature flag, so this is
                      dark for anyone without it either way.
                      See V3_MIGRATION_TRACKER.md · /v3/payments/payee-engagements.

                    {featureFlags.data?.rateCardOverride && (
                      <RateCardViewWidget
                        payeeId={payeeId}
                        payeeEngagementId={queryEngagementData.id as string}
                        context="contractor-assignment"
                        canCustomize={true}
                      />
                    )}
                    */}
                    <PayeeIntegrationWidget
                      isInactive={isInactive}
                      payeeId={payeeId}
                      engagementId={queryEngagementData.id as string}
                      // TODO(v3-blocker): the QuickBooks mapping is WRITE-ONLY on v3.
                      // `PayeeEngagementUpdate` accepts `quickbooksVendorId` /
                      // `quickbooksExpenseAccountId` (SDK 3.1.1), but the
                      // `PayeeEngagement` read model exposes neither, so there is no
                      // way to read the current per-engagement mapping back. v1 served
                      // it from `payerOwnedData.integration.quickbooks`. `Payee` does
                      // carry a payee-level `quickbooksVendorId`, but that is the wrong
                      // granularity and there is no expense-account equivalent at all.
                      // Until the read fields are added the widget renders as unmapped.
                      vendorId={undefined}
                      expenseAccountId={undefined}
                    />

                    <WidgetWorkLogsPayerView
                      filter={{
                        payeeId,
                        engagementId: queryEngagementData.engagementId
                      }}
                    />
                    <ResourceHistory
                      resources={historyResources}
                      xWingspanAccount={xWingspanAccount}
                      onViewAll={() => {
                        history.push(
                          buildEngagementHistoryPath(payeeId, engagementId)
                        );
                      }}
                    />
                  </WSList>
                </WSGrid.Item>
              </WSGrid>
            </WSList>
          );
        }}
      </WSQueries>
    </WSPage>
  );
};
